import { Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface Props {
  title: string;
  description: string;
  children?: React.ReactNode;
  /** Optional secondary action (e.g. text link or outline button) shown below primary action. */
  secondaryAction?: React.ReactNode;
  compact?: boolean;
}

const MattersEmptyView = ({
  title,
  description,
  children,
  secondaryAction,
  compact = false,
}: Props) => {
  const Wrapper = compact ? CompactPaper : EmptyPaper;
  const hasActions = Boolean(children ?? secondaryAction);

  return (
    <Wrapper variant="outlined">
      <Title variant="body1">{title}</Title>
      <Description variant="body2">{description}</Description>
      {hasActions ? (
        <ActionsWrapper>
          {children}
          {secondaryAction ? (
            <SecondaryActionWrapper>{secondaryAction}</SecondaryActionWrapper>
          ) : null}
        </ActionsWrapper>
      ) : null}
    </Wrapper>
  );
};

const EmptyPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(8, 4),
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: '1px solid',
  borderColor: theme.palette.divider,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(12, 6),
  },
}));

const CompactPaper = styled(Paper)(({ theme }) => ({
  padding: `${theme.spacing(6)} ${theme.spacing(4)}`,
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: 28,
}));

const Description = styled(Typography)(({ theme }) => ({
  marginBottom: 0,
  color: theme.palette.text.secondary,
}));

const ActionsWrapper = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(4),
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const SecondaryActionWrapper = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(1),
  alignItems: 'center',
}));

export { MattersEmptyView };
